import React from 'react';
import { StageSection, Prose, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'L1↔L2 Messaging & Settlement',
    tagline: 'Two chains, one asynchronous bridge between them.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - why L1<->L2 messages are asynchronous rather than an atomic call
//   Q1 - the difference between a transaction being "accepted on L2" and "accepted on L1"
export const blocks: StageBlock[] = [
    content('hero', () => (
        <StageSection gradientBorder eyebrow="Stage 7 of 8" title="L1 and L2 talk through a message bridge, not a direct call">
            <Prose>
                Ethereum (L1) and Starknet (L2) are separate execution environments, so a contract on one side can't
                directly call a contract on the other the way two Ethereum contracts call each other in the same
                transaction. Instead, Starknet defines a message-passing bridge between the two.
            </Prose>
            <FactGrid items={[
                ['L1 → L2', 'An L1 contract sends a message that becomes available for an L2 contract to consume once the corresponding Starknet block is processed, not instantly, and not atomically with the L1 transaction.'],
                ['L2 → L1', 'An L2 contract sends a message that an L1 contract can consume only after the batch containing it has been proven and its state root accepted by Ethereum.'],
            ]} />
        </StageSection>
    )),

    question(0),

    content('finality-tiers', () => (
        <StageSection title={'Two different notions of "final"'}>
            <Prose>
                A Starknet transaction can reach two distinct milestones. It's first <strong>accepted on L2</strong>{' '}
                once the sequencer includes it and later provable: fast, and reasonably reliable, but still
                resting on trust in the sequencer's ordering until proven. It's later <strong>accepted on L1</strong>{' '}
                once the batch containing it has been proven and Ethereum's verifier has accepted the new state
                root; at that point it inherits Ethereum's own security guarantees.
            </Prose>
            <Prose>
                Anything that depends on Ethereum-level finality (most importantly, consuming an L2 → L1 message)
                has to wait for that second milestone, not the first.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('why-it-matters', () => (
        <StageSection title="Why the asynchrony matters for contract design">
            <Prose>
                Because a message isn't consumed atomically with the transaction that sent it, a contract that
                assumes cross-layer calls behave like same-chain calls can be led into unsafe states: acting as if a
                deposit landed before the corresponding L2 message was actually consumed, or allowing a message to
                be replayed if consumption isn't tracked carefully on the receiving side. The cross-contract and
                upgrade-focused Cairo security challenges ahead build directly on this asynchronous, trust-but-verify
                relationship between the two layers.
            </Prose>
        </StageSection>
    )),
];
